import { formatScheduleTime, localizedMarketField, normalizeMarketProse, scheduleDisplayStatus, triggerReasonKey, parseTimestamp } from "../lib/api";
import { useI18n } from "../i18n";
import { TrendingUp, TrendingDown, Minus, AlertTriangle, Sparkles } from "lucide-react";

const SENT = {
  BULLISH: { color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/40", Icon: TrendingUp },
  BEARISH: { color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/40", Icon: TrendingDown },
  NEUTRAL: { color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/40", Icon: Minus },
};

export default function MarketSummary({ data, loading, schedule, now = Date.now(), statusAvailable }) {
  const { t, lang } = useI18n();

  if (loading && !data) {
    return <div className="border border-border/60 rounded-sm bg-[#09090B] p-5 h-full shimmer min-h-[180px]" />;
  }
  if (!data) return null;
  const s = SENT[data.sentiment] || SENT.NEUTRAL;
  const Icon = s.Icon;
  const scheduleStatus = scheduleDisplayStatus(schedule, "market_context", now);
  const scheduleReason = schedule?.market_context?.last_trigger_reason;
  const reasonMatches = data.trigger_reason || (
    data.generated_at && schedule?.market_context?.last_completed_at &&
    Math.abs((parseTimestamp(data.generated_at) || 0) - (parseTimestamp(schedule.market_context.last_completed_at) || 0)) <= 5 * 60 * 1000
      ? scheduleReason
      : null
  );
  const reasonKey = reasonMatches ? triggerReasonKey(reasonMatches) : null;
  const reasonLabelKey = {
    scheduled_update: "scheduled",
    news_triggered: "headline",
    price_triggered: "price",
    schema_upgrade: "schema",
  }[reasonKey];
  const reasonLabel = reasonLabelKey ? t(`market_reason_${reasonLabelKey}`) : null;

  return (
    <div className={`border ${s.border} rounded-sm bg-[#09090B] p-3 sm:p-5 h-full relative overflow-hidden`} data-testid="market-summary">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-emerald-400" strokeWidth={1.5} />
          <span className="font-display font-bold text-sm">{t("market_context")}</span>
        </div>
        <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-sm ${s.bg} ${s.color} font-mono text-[10px] uppercase tracking-widest`}>
          <Icon className="w-3 h-3" strokeWidth={2} />
          {t(data.sentiment.toLowerCase())}
        </div>
      </div>

      <div className="mb-3 flex flex-col items-start gap-0.5 text-[10px] font-mono text-zinc-500" data-testid="market-context-status">
        <span>{t("last_update")} {formatScheduleTime(schedule?.market_context?.last_completed_at, schedule?.timezone)}</span>
        <span>{t("next_update")} {formatScheduleTime(schedule?.market_context?.next_update_at, schedule?.timezone)}</span>
        {reasonLabel && <span data-testid="market-context-update-reason">{reasonLabel}</span>}
        <span className="text-white">
          {statusAvailable === false || scheduleStatus === "unavailable"
            ? t("status_unavailable")
            : scheduleStatus === "updating"
              ? t("updating")
              : scheduleStatus === "failed"
                ? t("update_temporarily_unavailable")
                : scheduleStatus === "delayed"
                  ? t("update_delayed")
                  : scheduleStatus}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Score */}
        <div className="md:col-span-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Score</div>
          <div className="flex items-baseline gap-2">
            <div className={`font-mono text-4xl font-bold ${s.color}`} data-testid="market-score">{data.score}</div>
            <div className="text-xs text-zinc-500 font-mono">/ 100</div>
          </div>
          <div className="mt-2 h-1.5 bg-white/5 rounded-sm overflow-hidden">
            <div
              className="h-full"
              style={{
                width: `${data.score}%`,
                background: data.score > 65 ? "#22C55E" : data.score < 35 ? "#EF4444" : "#F59E0B",
              }}
            />
          </div>
          <div className="text-[10px] text-zinc-500 font-mono mt-1 flex justify-between">
            <span>Fear</span><span>Greed</span>
          </div>
        </div>

        {/* Headline & drivers */}
        <div className="md:col-span-2 space-y-2">
          <div className="text-sm text-white leading-snug font-medium">{normalizeMarketProse(localizedMarketField(data.headline, lang, t("unavailable")))}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 mb-1">{t("drivers")}</div>
              <ul className="space-y-0.5">
                {(data.drivers || []).map((d, i) => (
                  <li key={i} className="text-xs text-zinc-300 leading-snug">→ {normalizeMarketProse(localizedMarketField(d, lang, t("unavailable")))}</li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-red-400 mb-1 flex items-center gap-1">
                <AlertTriangle className="w-2.5 h-2.5" /> {t("risks")}
              </div>
              <ul className="space-y-0.5">
                {(data.risks || []).map((r, i) => (
                  <li key={i} className="text-xs text-zinc-300 leading-snug">! {normalizeMarketProse(localizedMarketField(r, lang, t("unavailable")))}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
